// Reusable address search input with server-backed autocomplete suggestions.
// Works as a single-line field (opportunity customer address, proposal
// contact block) or as the search bar above structured address fields.

import { useId, useRef } from "react";
import { useAddressAutocomplete } from "./useAddressAutocomplete";
import type { AddressAutocompletePick } from "@/lib/addressAutocompleteClient";

type Props = {
  value: string;
  onChange: (next: string) => void;
  onPick?: (pick: AddressAutocompletePick) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputId?: string;
  /** Shown when the server has no Places key configured. */
  hintWhenDisabled?: string;
};

export function AddressAutocompleteInput({
  value,
  onChange,
  onPick,
  placeholder = "Start typing an address…",
  disabled = false,
  className = "w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100",
  inputId,
  hintWhenDisabled = "Autocomplete is unavailable — enter the address manually.",
}: Props) {
  const autoId = useId();
  const id = inputId ?? autoId;
  const listId = `${id}-suggestions`;
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ac = useAddressAutocomplete();

  function handleChange(next: string) {
    onChange(next);
    ac.setQuery(next);
  }

  async function chooseSuggestion(placeId: string) {
    const suggestion = ac.suggestions.find((s) => s.placeId === placeId);
    if (!suggestion) return;
    try {
      const pick = await ac.pick(suggestion);
      onChange(pick.formatted);
      onPick?.(pick);
    } catch {
      // error surfaced via ac.error
    }
  }

  const showList = ac.open && ac.suggestions.length > 0 && !disabled;
  const providerDisabled = ac.provider === "disabled" && value.trim().length >= 3;

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        className={className}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          if (ac.suggestions.length > 0) ac.setOpen(true);
          ac.setQuery(value);
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => ac.setOpen(false), 150);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {ac.busy ? (
        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">
          …
        </div>
      ) : null}
      {showList ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          {ac.suggestions.map((s) => (
            <li key={s.placeId} role="option">
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void chooseSuggestion(s.placeId)}
              >
                <div className="font-medium text-gray-900 dark:text-zinc-100">{s.mainText}</div>
                {s.secondaryText ? (
                  <div className="text-xs text-gray-500 dark:text-zinc-400">{s.secondaryText}</div>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {ac.error ? (
        <p className="mt-1 text-xs text-red-600 dark:text-red-300">{ac.error}</p>
      ) : providerDisabled ? (
        <p className="mt-1 text-xs text-gray-400 dark:text-zinc-500">{hintWhenDisabled}</p>
      ) : null}
    </div>
  );
}
